/**
 * /platform/roles/:id - single role's details, with inline edit +
 * delete flows for custom roles.
 *
 * View mode (default):
 *   - Header (icon + name + slug + system/custom badge + counts)
 *   - Permission grants grouped by category
 *   - Edit / Delete buttons (custom roles only - backend enforces
 *     the same gate, this is a UX guard)
 *
 * Edit mode (when ``isEditing``):
 *   - <RoleEditor mode="edit" .../> renders the form. On save it
 *     refreshes the cached detail and flips back to view mode.
 *
 * System roles render as read-only. The hint paragraph explains
 * that they're managed by migration; the API would 409 on a write
 * attempt regardless.
 */

import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Pencil, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { RolePermissionGrant } from "@tilt/admin-sdk";

import { RoleEditor } from "@/components/RoleEditor";
import { useDeleteRoleMutation } from "@/components/role-mutations";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { usePageTitle } from "@/lib/use-page-title";
import { cn } from "@/lib/utils";

export function RoleDetailPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  usePageTitle("Role detail");
  const navigate = useNavigate();
  const profile = useRoleProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPerms, setIsEditingPerms] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMut = useDeleteRoleMutation();

  const query = useQuery({
    queryKey: ["roles", "detail", id],
    queryFn: () => apiClient.getRole(id),
    enabled: id.length > 0,
    retry: false,
  });

  if (query.isLoading) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading role…
      </p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate("/platform/roles")}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back
        </button>
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
          {describe(query.error)}
        </div>
      </div>
    );
  }

  const { role, grants } = query.data;
  const isSystem = role.is_system;
  // V0074 - three role tiers; "platform-custom" rows have is_system=false
  // AND partner_id=null. Used for the badge color + "Tier" copy and for
  // gating the Workplace metadata line.
  const isPlatformCustom = !isSystem && role.partner_id === null;

  if (isEditing && !isSystem) {
    return (
      <div className="space-y-4">
        <Link
          to="/platform/roles"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to roles
        </Link>
        <RoleEditor
          mode={{
            kind: "edit",
            roleId: role.id,
            partnerId: role.partner_id ?? "",
            partnerName: role.partner_name,
            partnerPath: role.partner_path,
            initialName: role.name,
            initialDescription: role.description,
            initialGrants: grants,
            initialAssignablePaths: role.assignable_to_partner_paths,
          }}
          onCancel={() => setIsEditing(false)}
          onSaved={() => {
            setIsEditing(false);
            query.refetch();
          }}
        />
      </div>
    );
  }

  if (isEditingPerms && isSystem) {
    return (
      <div className="space-y-4">
        <Link
          to="/platform/roles"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to roles
        </Link>
        <RoleEditor
          mode={{
            kind: "system-grants",
            roleId: role.id,
            roleName: role.name,
            initialGrants: grants,
          }}
          onCancel={() => setIsEditingPerms(false)}
          onSaved={() => {
            setIsEditingPerms(false);
            query.refetch();
          }}
        />
      </div>
    );
  }

  const grouped = groupByCategory(grants);
  const assignablePaths = role.assignable_to_partner_paths ?? [];

  async function handleDelete(): Promise<void> {
    try {
      await deleteMut.mutateAsync(role.id);
      navigate("/platform/roles");
    } catch {
      /* error surfaces inline below */
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/platform/roles"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to roles
        </Link>
        <div className="mt-2 flex flex-wrap items-start gap-3">
          <ShieldCheck
            className={cn(
              "mt-1 h-5 w-5",
              isSystem
                ? "text-indigo-600"
                : isPlatformCustom
                  ? "text-purple-600"
                  : "text-emerald-600",
            )}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{role.name}</h1>
            <p className="font-mono text-xs text-muted-foreground">{role.slug}</p>
            {role.description && (
              <p className="mt-1 text-sm text-muted-foreground">{role.description}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 font-semibold uppercase tracking-wide",
                  isSystem
                    ? "bg-indigo-100 text-indigo-800"
                    : isPlatformCustom
                      ? "bg-purple-100 text-purple-800"
                      : "bg-emerald-100 text-emerald-800",
                )}
              >
                {isSystem ? "System" : isPlatformCustom ? "Platform" : "Partner"}
              </span>
              {isSystem ? (
                <span className="text-muted-foreground">Managed by migration</span>
              ) : isPlatformCustom ? (
                <span className="text-muted-foreground">Visible to every partner</span>
              ) : (
                <span className="text-muted-foreground">
                  {role.partner_name ?? "-"} · {role.partner_path}
                </span>
              )}
              <span className="text-muted-foreground">
                {grants.length} permission{grants.length === 1 ? "" : "s"}
              </span>
            </div>
            {/* V0074 - assignment allowlist chip list. */}
            <div className="mt-2 flex flex-wrap items-start gap-2 text-[11px]">
              <span className="font-medium uppercase tracking-wide text-muted-foreground">
                Assignable in
              </span>
              {assignablePaths.length === 0 ? (
                <span className="text-muted-foreground">
                  Anyone who can see this role can assign it
                </span>
              ) : (
                <ul className="flex flex-wrap gap-1">
                  {assignablePaths.map((p) => (
                    <li
                      key={p}
                      className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            {isSystem && isSuperAdmin(profile) && (
              <button
                type="button"
                onClick={() => setIsEditingPerms(true)}
                className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <Pencil className="h-3 w-3" aria-hidden="true" />
                Edit permissions
              </button>
            )}
            {!isSystem && (
              <>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
                >
                  <Pencil className="h-3 w-3" aria-hidden="true" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-background px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" aria-hidden="true" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {confirmDelete && !isSystem && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs">
          <p className="font-medium text-red-900">Delete this role?</p>
          <p className="mt-1 text-red-800">
            People who currently carry it keep their grant rows but the role is hidden from the
            catalog. Soft-delete only - a super_admin can restore it from SQL if needed.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              disabled={deleteMut.isPending}
              className="rounded-md border bg-background px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleteMut.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deleteMut.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" aria-hidden="true" />
              )}
              Confirm delete
            </button>
          </div>
          {deleteMut.isError && <p className="mt-2 text-red-800">{describe(deleteMut.error)}</p>}
        </div>
      )}

      {grants.length === 0 ? (
        <p className="rounded-md border border-dashed bg-muted/30 p-4 text-xs text-muted-foreground">
          No permissions granted on this role yet.
        </p>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <CategorySection key={g.category} category={g.category} grants={g.grants} />
          ))}
        </div>
      )}

      {isSystem && (
        <p className="rounded-md border border-dashed bg-muted/20 p-3 text-[11px] text-muted-foreground">
          System roles ship with the platform. A super_admin can edit their permission grants here.
          To change name, slug, or pool membership, land a SQL change in tilt-runtime/migrations.
        </p>
      )}
    </div>
  );
}

interface CategoryGroup {
  readonly category: string;
  readonly grants: readonly RolePermissionGrant[];
}

function groupByCategory(grants: readonly RolePermissionGrant[]): readonly CategoryGroup[] {
  const map = new Map<string, RolePermissionGrant[]>();
  for (const g of grants) {
    const existing = map.get(g.category);
    if (existing) {
      existing.push(g);
    } else {
      map.set(g.category, [g]);
    }
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, gs]) => ({ category, grants: gs }));
}

function CategorySection(props: {
  readonly category: string;
  readonly grants: readonly RolePermissionGrant[];
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-lg border bg-card">
      <header className="border-b bg-muted/30 px-4 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          {props.category}
        </h2>
      </header>
      <ul className="divide-y">
        {props.grants.map((g) => (
          <li
            key={g.permission_code}
            className="flex flex-wrap items-start justify-between gap-3 px-4 py-2"
          >
            <div className="min-w-0">
              <p className="font-mono text-xs text-strong">{g.permission_code}</p>
              <p className="text-xs text-muted-foreground">{g.description}</p>
            </div>
            {g.supports_scope && g.scope && (
              <span
                className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700"
                title="Scope this role grants for the permission"
              >
                {g.scope}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
